import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { dailyAccountAPI } from '../services/api';
import { Store, DailyAccount } from '../types';
import SankeyDiagram from './SankeyDiagram';

interface CashFlowDiagramProps {
  selectedStore?: Store | null;
  storeId?: string | number; // Support for "all" or specific store ID
  dateRange?: {
    start: string;
    end: string;
  };
  totals?: {
    totalSales: number; // Finance Dashboard total income
    totalPurchase: number; // Finance Dashboard total purchases
  };
}

interface CashFlowData {
  openingCash: number;
  openingBankBalance: number;
  totalBankCredits: number;
  totalBankDebits: number;
  totalExpense: number;
  closingCash: number;
  closingBankBalance: number;
  netCashFlow: number;
  netBankFlow: number;
  totalInflow: number;
  totalOutflow: number;
}


const CashFlowDiagram: React.FC<CashFlowDiagramProps> = ({ selectedStore, storeId, dateRange, totals }) => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [cashFlowData, setCashFlowData] = useState<CashFlowData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync with dateRange prop from parent (Finance Dashboard)
  useEffect(() => {
    if (dateRange) {
      setStartDate(dayjs(dateRange.start));
      setEndDate(dayjs(dateRange.end));
    }
  }, [dateRange]);

  // Load cash flow data when filters change
  useEffect(() => {
    loadCashFlowData();
  }, [selectedStore, storeId, startDate, endDate]);

  const getStoreIdForApi = () => {
    if (storeId !== undefined) {
      return storeId === 'all' ? undefined : storeId.toString();
    }
    return selectedStore ? selectedStore.id.toString() : undefined;
  };

  const getDisplayStoreName = () => {
    if (storeId === 'all') return 'All Stores';
    if (selectedStore) return selectedStore.store_name;
    return 'Unknown Store';
  };

  const loadCashFlowData = async () => {
    const apiStoreId = getStoreIdForApi();
    
    console.log('Loading cash flow data with params:', {
      apiStoreId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    });

    setLoading(true);
    setError(null);
    
    try {
      // Try the optimized endpoint first
      const cashFlowSummary = await dailyAccountAPI.getCashFlowSummary({
        store_id: apiStoreId,
        date_from: startDate.format('YYYY-MM-DD'),
        date_to: endDate.format('YYYY-MM-DD')
      });
      
      console.log('Cash flow summary from API:', cashFlowSummary);
      console.log('API response structure:', JSON.stringify(cashFlowSummary, null, 2));
      
      if (cashFlowSummary && (cashFlowSummary.data || cashFlowSummary.summary)) {
        const convertedData = convertApiDataToCashFlowData(cashFlowSummary);
        console.log('Converted cash flow data:', convertedData);
        setCashFlowData(convertedData);
      } else {
        throw new Error('Invalid response format from cash flow summary API');
      }
    } catch (apiError) {
      console.warn('Cash flow summary API failed, falling back to daily accounts:', apiError);
      
      try {
        // Fallback to getting all daily accounts and calculating client-side
        const accounts = await dailyAccountAPI.getAll({
          date_from: startDate.format('YYYY-MM-DD'),
          date_to: endDate.format('YYYY-MM-DD'),
          store_id: apiStoreId
        });
        
        console.log('Daily accounts fallback data:', accounts);
        
        if (accounts && accounts.length > 0) {
          const calculatedData = calculateCashFlowDataFromAccounts(accounts);
          setCashFlowData(calculatedData);
        } else {
          // Show sample data for testing
          setCashFlowData({
            openingCash: 0,
            openingBankBalance: 536142.79,
            totalBankCredits: 177500.31,
            totalBankDebits: 106929,
            totalExpense: 9267,
            closingCash: 132009,
            closingBankBalance: 606714.1,
            netCashFlow: 132009,
            netBankFlow: 70571.31,
            totalInflow: 177500.31,
            totalOutflow: 116196
          });
        }
      } catch (fallbackError) {
        console.error('Both APIs failed:', fallbackError);
        setError('Failed to load cash flow data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const convertApiDataToCashFlowData = (apiData: any): CashFlowData => {
    const data = apiData.data || apiData; // Handle both response formats
    return {
      openingCash: data.opening_balances?.cash || 0,
      openingBankBalance: data.opening_balances?.bank || 0,
      totalBankCredits: data.flows?.bank_credits || 0,
      totalBankDebits: data.flows?.bank_debits || 0,
      totalExpense: data.flows?.total_expenses || 0,
      closingCash: data.closing_balances?.cash || 0,
      closingBankBalance: data.closing_balances?.bank || 0,
      netCashFlow: data.flows?.net_cash_flow || 0,
      netBankFlow: data.flows?.net_bank_flow || 0,
      totalInflow: data.summary?.total_inflows || 0,
      totalOutflow: data.summary?.total_outflows || 0
    };
  };

  const calculateCashFlowDataFromAccounts = (accounts: DailyAccount[]): CashFlowData => {
    if (accounts.length === 0) {
      return {
        openingCash: 0, openingBankBalance: 0, totalBankCredits: 0, totalBankDebits: 0,
        totalExpense: 0, closingCash: 0, closingBankBalance: 0, netCashFlow: 0,
        netBankFlow: 0, totalInflow: 0, totalOutflow: 0
      };
    }

    // Sort by date to get first and last
    const sortedAccounts = accounts.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const firstDay = sortedAccounts[0];
    const lastDay = sortedAccounts[sortedAccounts.length - 1];

    // Sum totals across all days
    const totalBankCredits = accounts.reduce((sum, acc) => sum + (Number(acc.total_bank_credits) || 0), 0);
    const totalBankDebits = accounts.reduce((sum, acc) => sum + (Number(acc.total_bank_debits) || 0), 0);
    const totalExpense = accounts.reduce((sum, acc) => sum + (Number(acc.total_expense) || 0), 0);

    // Opening balances from first day
    const openingCash = Number(firstDay.opening_cash) || 0;
    const openingBankBalance = Number(firstDay.opening_bank_balance) || 0;

    // Closing balances from last day
    const closingCash = Number(lastDay.closing_cash) || 0;
    const closingBankBalance = Number(lastDay.closing_bank_balance) || 0;

    const netCashFlow = closingCash - openingCash;
    const netBankFlow = closingBankBalance - openingBankBalance;
    const totalInflow = totalBankCredits;
    const totalOutflow = totalBankDebits + totalExpense;

    return {
      openingCash, openingBankBalance, totalBankCredits, totalBankDebits, totalExpense,
      closingCash, closingBankBalance, netCashFlow, netBankFlow, totalInflow, totalOutflow
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };


  if (!selectedStore && storeId === undefined) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            Please select a store to view cash flow diagram.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Cash Flow Diagram
            </Typography>
          </Box>
          
          {/* Only show date pickers when not controlled by parent (Finance Dashboard) */}
          {!dateRange && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => newValue && setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => newValue && setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`Store: ${getDisplayStoreName()}`}
              color="primary" 
              variant="outlined" 
            />
          </Box>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {cashFlowData && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
            Cash Flow Statement - Sankey Diagram
          </Typography>
          <SankeyDiagram
            openingCash={cashFlowData.openingCash}
            openingBankBalance={cashFlowData.openingBankBalance}
            totalBankCredits={cashFlowData.totalBankCredits}
            totalBankDebits={cashFlowData.totalBankDebits}
            totalExpense={cashFlowData.totalExpense}
            closingCash={cashFlowData.closingCash}
            closingBankBalance={cashFlowData.closingBankBalance}
            netChange={cashFlowData.closingCash + cashFlowData.closingBankBalance - cashFlowData.openingCash - cashFlowData.openingBankBalance}
            salesTotal={totals?.totalSales}
            purchaseTotal={totals?.totalPurchase}
          />
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Period: {startDate.format('DD/MM/YYYY')} to {endDate.format('DD/MM/YYYY')}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default CashFlowDiagram;
